/**
 * OutputWindow Component
 *
 * Displays the image for the currently active beat based on playhead position.
 *
 * HOW IT WORKS:
 * =============
 * 1. Get the current playhead time from the playback store
 * 2. Calculate which beat is active at that time
 * 3. Display that beat's image (or a placeholder if no image)
 *
 * The image updates smoothly during playback as the playhead moves
 * from one beat to the next.
 */

import { useMemo, useEffect, memo, CSSProperties, useState, useRef, useCallback } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useCurrentTime } from '@/stores/playbackStore';
import { TimelineBeat, TitleNode } from '@/types';
import { getImageUrl } from '@/utils/browserAPI';
import { CameraOverlayRenderer } from './CameraOverlayRenderer';
import { TextOverlayRenderer } from './TextOverlayRenderer';
import { TitleRenderer } from './TitleRenderer';

/**
 * OutputWindow Component
 */
function OutputWindow() {
  // ---------------------------------------------------------------------------
  // STORE ACCESS
  // ---------------------------------------------------------------------------

  // Subscribe to nodes directly for reactivity (not getSortedNodes function)
  const nodes = useProjectStore((state) => state.nodes);
  const titleNodes = useProjectStore((state) => state.titleNodes);
  const projectPath = useProjectStore((state) => state.projectPath);
  const updateNode = useProjectStore((state) => state.updateNode);
  const aspectRatio = useProjectStore((state) => state.aspectRatio);
  const currentTime = useCurrentTime();

  // ---------------------------------------------------------------------------
  // DRAG STATE
  // ---------------------------------------------------------------------------

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const aspectRatioContainerRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // CALCULATE TIMELINE BEATS
  // ---------------------------------------------------------------------------

  /**
   * Build a list of beats with their time ranges
   * Subscribes to `nodes` directly so this recalculates when nodes change.
   */
  const timelineBeats: TimelineBeat[] = useMemo(() => {
    // Filter out nodes that are on the shelf, then sort by x-position for timeline order
    const filtered = nodes.filter((node) => !node.data.onShelf);
    const sorted = [...filtered].sort((a, b) => a.position.x - b.position.x);
    let currentStart = 0;

    return sorted.map((node, index) => {
      const startTime = currentStart;
      const endTime = startTime + node.data.duration;
      currentStart = endTime;

      return {
        node,
        startTime,
        endTime,
        index,
      };
    });
  }, [nodes]);

  // ---------------------------------------------------------------------------
  // FIND ACTIVE BEAT
  // ---------------------------------------------------------------------------

  /**
   * Find which beat is active at the current playhead time
   */
  const activeBeat = useMemo(() => {
    // Find the beat that contains the current time
    const beat = timelineBeats.find(
      (b) => currentTime >= b.startTime && currentTime < b.endTime
    );

    // If we're at the very end, show the last beat
    if (!beat && timelineBeats.length > 0) {
      const lastBeat = timelineBeats[timelineBeats.length - 1];
      if (currentTime >= lastBeat.startTime) {
        return lastBeat;
      }
    }

    return beat;
  }, [timelineBeats, currentTime]);

  // ---------------------------------------------------------------------------
  // FIND ACTIVE TITLES
  // ---------------------------------------------------------------------------

  /**
   * Find which titles are active at the current playhead time
   */
  const activeTitles = useMemo(() => {
    // Find all titles that contain the current time
    return titleNodes.filter((title) => {
      const startTime = title.data.startTime;
      const endTime = startTime + title.data.duration;
      return currentTime >= startTime && currentTime < endTime;
    });
  }, [currentTime, titleNodes]);

  // ---------------------------------------------------------------------------
  // GET IMAGE URL
  // ---------------------------------------------------------------------------

  /**
   * Resolve the image path to a displayable URL
   * Works with both Electron (file://) and browser (blob:) URLs
   */
  const imageUrl = useMemo(() => {
    if (!activeBeat?.node.data.imagePath) return null;

    const imagePath = activeBeat.node.data.imagePath;

    // Try browser API first (handles blob: URLs)
    const browserUrl = getImageUrl(imagePath);
    if (browserUrl) return browserUrl;

    // Fall back to Electron file:// URL
    if (!projectPath) return null;
    const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
    const absolutePath = `${projectDir}/${imagePath}`.replace(/\\/g, '/');
    return `file://${absolutePath}`;
  }, [activeBeat, projectPath]);

  // ---------------------------------------------------------------------------
  // ZOOM CALCULATION
  // ---------------------------------------------------------------------------

  /**
   * Calculate zoom from active beat's imageScale
   * This is calculated synchronously during render to avoid flash
   */
  const zoom = useMemo(() => {
    return activeBeat?.node.data.imageScale || 1;
  }, [activeBeat?.node.data.imageScale]);

  // ---------------------------------------------------------------------------
  // DRAG HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Check if the image can be dragged
   * When zoomed in, allow dragging in both directions
   * When not zoomed, only allow dragging in the direction where image doesn't fit
   */
  const canDrag = useMemo(() => {
    if (!imageRef.current || !aspectRatio || !imageLoaded) return { canDragX: false, canDragY: false };

    const img = imageRef.current;
    if (!img.naturalWidth || !img.naturalHeight) return { canDragX: false, canDragY: false };

    // If zoomed in (scale > 1), allow dragging in both directions
    if (zoom > 1) {
      return { canDragX: true, canDragY: true };
    }

    const imageAspectRatio = img.naturalWidth / img.naturalHeight;
    const projectAspectRatio = aspectRatio === '16:9' ? 16 / 9 : aspectRatio === '9:16' ? 9 / 16 : 1;

    // Allow a small tolerance for aspect ratio comparison (0.01)
    const aspectDiff = Math.abs(imageAspectRatio - projectAspectRatio);

    if (aspectDiff < 0.01) {
      // Aspect ratios match - no dragging needed
      return { canDragX: false, canDragY: false };
    }

    // Determine which direction can be dragged based on aspect ratio difference
    if (imageAspectRatio > projectAspectRatio) {
      // Image is wider - can drag horizontally
      return { canDragX: true, canDragY: false };
    } else {
      // Image is taller - can drag vertically
      return { canDragX: false, canDragY: true };
    }
  }, [aspectRatio, imageLoaded, zoom]); // Re-calculate when aspect ratio, image loads, or zoom changes

  /**
   * Handle mouse down on image - start dragging
   */
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const { canDragX, canDragY } = canDrag;
    if (!canDragX && !canDragY) return;

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  }, [canDrag]);

  /**
   * Handle mouse move - update image position
   */
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !activeBeat || !imageRef.current) return;

    const { canDragX, canDragY } = canDrag;
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    // Get the displayed image dimensions
    const imageRect = imageRef.current.getBoundingClientRect();

    // Get current offsets (default to 0)
    const currentOffsetX = activeBeat.node.data.imageOffsetX || 0;
    const currentOffsetY = activeBeat.node.data.imageOffsetY || 0;

    // Calculate new offsets (-1 to 1 range)
    let newOffsetX = currentOffsetX;
    let newOffsetY = currentOffsetY;

    // Sensitivity: lower = more precise control
    const sensitivity = 1;

    if (canDragX) {
      // For horizontal dragging, normalize based on image width
      // Dragging right should show more of the left side (decrease offset)
      const normalizedDeltaX = -(deltaX / imageRect.width) * sensitivity;
      newOffsetX = Math.max(-1, Math.min(1, currentOffsetX + normalizedDeltaX));
    }

    if (canDragY) {
      // For vertical dragging, normalize based on image height
      // Dragging down should show more of the top (decrease offset)
      const normalizedDeltaY = -(deltaY / imageRect.height) * sensitivity;
      newOffsetY = Math.max(-1, Math.min(1, currentOffsetY + normalizedDeltaY));
    }

    // Update the node with new offsets
    updateNode(activeBeat.node.id, {
      imageOffsetX: newOffsetX,
      imageOffsetY: newOffsetY,
    });

    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, activeBeat, dragStart, canDrag, updateNode]);

  /**
   * Handle mouse up - stop dragging
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * Handle mouse wheel - adjust zoom and save to node
   */
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!activeBeat) return;

    e.preventDefault();
    const delta = -e.deltaY / 1000; // Normalize wheel delta
    const currentScale = activeBeat.node.data.imageScale || 1;
    const newScale = Math.max(1, Math.min(3, currentScale + delta)); // 1x to 3x zoom

    updateNode(activeBeat.node.id, { imageScale: newScale });
  }, [activeBeat, updateNode]);

  /**
   * Reset zoom and position to defaults
   */
  const handleResetZoom = useCallback(() => {
    if (!activeBeat) return;

    updateNode(activeBeat.node.id, {
      imageScale: 1,
      imageOffsetX: 0,
      imageOffsetY: 0,
    });
  }, [activeBeat, updateNode]);

  /**
   * Set up global mouse event listeners for dragging
   */
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  /**
   * Reset image state when image URL changes
   */
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [imageUrl]);

  /**
   * Track aspect ratio container dimensions for overlay positioning
   */
  useEffect(() => {
    const container = aspectRatioContainerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      setContainerDimensions({
        width: rect.width,
        height: rect.height,
      });
    };

    // Initial measurement
    updateDimensions();

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [aspectRatio, activeBeat]); // Re-measure when aspect ratio or beat changes

  /**
   * Update dimensions when image loads to ensure overlays render correctly
   */
  useEffect(() => {
    if (imageLoaded && aspectRatioContainerRef.current) {
      const rect = aspectRatioContainerRef.current.getBoundingClientRect();
      setContainerDimensions({
        width: rect.width,
        height: rect.height,
      });
    }
  }, [imageLoaded]);

  /**
   * Ensure dimensions are measured on mount with a small delay
   * This catches cases where the container renders after the initial useEffect
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (aspectRatioContainerRef.current) {
        const rect = aspectRatioContainerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setContainerDimensions({
            width: rect.width,
            height: rect.height,
          });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  /**
   * Handle image load error
   */
  const handleImageError = useCallback(() => {
    console.error('Failed to load image:', imageUrl);
    setImageError(true);
    setImageLoaded(false);
  }, [imageUrl]);

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--nb-bg-primary)',
    overflow: 'hidden',
    position: 'relative',
  };

  /**
   * Calculate transform based on zoom
   * Just apply zoom scale, let object-fit: cover handle the rest
   */
  const imageTransform = useMemo(() => {
    return `scale(${zoom})`;
  }, [zoom]);

  /**
   * Calculate object-position based on offset
   * Used for both zoomed and non-zoomed states
   */
  const objectPosition = useMemo(() => {
    if (!activeBeat) return 'center';

    const offsetX = activeBeat.node.data.imageOffsetX || 0;
    const offsetY = activeBeat.node.data.imageOffsetY || 0;

    // Convert -1 to 1 range to 0% to 100%
    const xPercent = ((offsetX + 1) / 2) * 100;
    const yPercent = ((offsetY + 1) / 2) * 100;

    return `${xPercent}% ${yPercent}%`;
  }, [activeBeat?.node.data.imageOffsetX, activeBeat?.node.data.imageOffsetY]);

  const { canDragX, canDragY } = canDrag;

  /**
   * Container that maintains aspect ratio
   */
  const aspectRatioContainerStyles: CSSProperties = useMemo(() => {
    const ratio = aspectRatio === '16:9' ? 16 / 9 : aspectRatio === '9:16' ? 9 / 16 : 1;

    // For 16:9 (landscape), constrain by width
    // For 9:16 (portrait), constrain by height
    // For 1:1 (square), fit within the container
    if (aspectRatio === '16:9') {
      return {
        position: 'relative',
        width: '100%',
        height: 'auto',
        aspectRatio: ratio,
        maxHeight: '100%',
        overflow: 'hidden',
      };
    } else if (aspectRatio === '9:16') {
      return {
        position: 'relative',
        width: 'auto',
        height: '100%',
        aspectRatio: ratio,
        maxWidth: '100%',
        overflow: 'hidden',
      };
    } else {
      // 1:1 square - fit within container
      return {
        position: 'relative',
        width: 'auto',
        height: '100%',
        aspectRatio: ratio,
        maxWidth: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      };
    }
  }, [aspectRatio]);

  const imageStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Always use cover
    objectPosition: objectPosition, // Position based on offsets
    transform: imageTransform, // Apply zoom scale only
    transformOrigin: objectPosition, // Transform from the current object position
    transition: 'none', // Disable all transitions to prevent animation
    cursor: canDragX || canDragY ? (isDragging ? 'grabbing' : 'grab') : 'default',
    userSelect: 'none',
    display: 'block',
  };

  const placeholderStyles: CSSProperties = {
    color: 'var(--nb-text-muted)',
    textAlign: 'center',
    padding: 'var(--nb-space-md)',
  };

  const dragOverlayStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 122, 204, 0.1)',
    border: '2px dashed var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const dragHintStyles: CSSProperties = {
    padding: 'var(--nb-space-sm) var(--nb-space-md)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-accent-primary)',
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 600,
  };

  const resetButtonStyles: CSSProperties = {
    position: 'absolute',
    top: 'var(--nb-space-sm)',
    right: 'var(--nb-space-sm)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    border: '1px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-text-primary)',
    fontSize: 'var(--nb-font-size-xs)',
    cursor: 'pointer',
    zIndex: 10,
  };

  const whiteBackgroundStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#ffffff',
    zIndex: 1,
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      style={containerStyles}
      title="Displays the storyboard image for the current beat at the playhead position"
    >
      {/* Aspect ratio container - always rendered for consistent layout */}
      <div
        ref={aspectRatioContainerRef}
        key={aspectRatio}
        style={aspectRatioContainerStyles}
        onWheel={handleWheel}
      >
        {/* White background for empty beats */}
        {activeBeat && !imageUrl && <div style={whiteBackgroundStyles} />}

        {imageUrl && !imageError ? (
          <>
            <img
              ref={imageRef}
              src={imageUrl}
              alt={activeBeat?.node.data.title || 'Storyboard frame'}
              style={imageStyles}
              draggable={false}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
              onMouseDown={handleMouseDown}
              title={
                canDragX || canDragY
                  ? `Click and drag to reposition image for "${activeBeat?.node.data.title}". Scroll to zoom.`
                  : `Storyboard frame for "${activeBeat?.node.data.title}". Scroll to zoom.`
              }
            />

            {/* Camera overlays (NEW) */}
            {activeBeat?.node.data.overlays && activeBeat.node.data.overlays.length > 0 && containerDimensions.width > 0 && (
              <CameraOverlayRenderer
                overlays={activeBeat.node.data.overlays}
                containerWidth={containerDimensions.width}
                containerHeight={containerDimensions.height}
              />
            )}

            {/* Text overlays (NEW) */}
            {activeBeat?.node.data.textOverlays && activeBeat.node.data.textOverlays.length > 0 && containerDimensions.width > 0 && (
              <TextOverlayRenderer
                textOverlays={activeBeat.node.data.textOverlays}
                containerWidth={containerDimensions.width}
                containerHeight={containerDimensions.height}
              />
            )}

            {/* Drag feedback overlay */}
            {isDragging && (
              <div style={dragOverlayStyles}>
                <div style={dragHintStyles}>
                  Repositioning Image
                </div>
              </div>
            )}

            {/* Reset button (show if zoom is not 1 or position is offset) */}
            {activeBeat && !isDragging && (zoom !== 1 || activeBeat.node.data.imageOffsetX !== 0 || activeBeat.node.data.imageOffsetY !== 0) && (
              <button
                style={resetButtonStyles}
                onClick={handleResetZoom}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                title="Reset zoom and position"
              >
                Reset
              </button>
            )}
          </>
        ) : (
          <div style={placeholderStyles}>
            {imageError ? (
              <>
                <p style={{ marginBottom: 'var(--nb-space-xs)', color: 'var(--nb-accent-error)' }}>Failed to load image</p>
                <p
                  style={{ fontSize: 'var(--nb-font-size-xs)' }}
                  title="The image file may be missing, corrupted, or in an unsupported format"
                >
                  The image for "{activeBeat?.node.data.title}" could not be loaded.
                </p>
                <p style={{ fontSize: 'var(--nb-font-size-xs)', marginTop: 'var(--nb-space-xs)' }}>
                  Try uploading a different image.
                </p>
              </>
            ) : activeBeat ? (
              <>
                <p style={{ marginBottom: 'var(--nb-space-xs)' }}>No image</p>
                <p
                  style={{ fontSize: 'var(--nb-font-size-xs)' }}
                  title="Select this beat in the Node View and add an image in the Properties Panel"
                >
                  Add an image to "{activeBeat.node.data.title}"
                </p>
              </>
            ) : (
              <>
                <p style={{ marginBottom: 'var(--nb-space-xs)' }}>No beats</p>
                <p
                  style={{ fontSize: 'var(--nb-font-size-xs)' }}
                  title="Create a new beat node by double-clicking anywhere in the Node View"
                >
                  Double-click in the Node View to create a beat
                </p>
              </>
            )}
          </div>
        )}

        {/* Title layer (separate from beats) - rendered on top of everything */}
        {activeTitles.length > 0 && containerDimensions.width > 0 && (
          <>
            {activeTitles.map((titleNode) => (
              <TitleRenderer
                key={titleNode.id}
                titleNode={titleNode}
                containerWidth={containerDimensions.width}
                containerHeight={containerDimensions.height}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default memo(OutputWindow);
